// src/features/user-portfolios/utils/performance.ts
//
// Shared client-side rebase logic for the portfolio performance chart AND the
// "Return vs {benchmark}" KPI card. Extracting this guarantees the KPI number
// and the chart curve always report the SAME period-over-period performance,
// and that the period-boundary conventions match the backend TWR engine
// (`calculate_time_series_performance`):
//
//   * YTD cutoff is Dec 31 of the previous year (NOT Jan 1), matching the
//     backend `ytd_date = date(end_date.year - 1, 12, 31)`.
//   * The baseline is the value ON/BEFORE the cutoff (last prior point), the
//     same convention as the backend `_twr_on_or_before`.
//   * Period windows are anchored to the LAST AVAILABLE SERVER date (the tail
//     of the series), not the local system clock, so data-lag never skews the
//     7D / 1M windows.

import type { ChartDataPoint } from '../api';

export const PERIODS = ['7D', '1M', '3M', '6M', 'YTD', '1Y', 'MAX'] as const;
export type Period = (typeof PERIODS)[number];

export interface RebasedChartPoint {
    date: string;
    formattedDate: string;
    Portfolio: number;
    Benchmark: number;
}

export interface RebasedPerformance {
    chartData: RebasedChartPoint[];
    // Period-over-period % returns (last available point vs the cutoff baseline).
    portPeriodPct: number;
    benchPeriodPct: number;
}

const EMPTY: RebasedPerformance = {
    chartData: [],
    portPeriodPct: 0,
    benchPeriodPct: 0,
};

/**
 * Rebase the raw cumulative TWR + benchmark series to a chosen timeframe so the
 * start of the window equals exactly 0%, and derive the matching period % for
 * both portfolio and benchmark.
 */
export const computeRebasedPerformance = (
    data: ChartDataPoint[],
    period: Period,
): RebasedPerformance => {
    if (!data || data.length === 0) return EMPTY;

    // Parse dates safely (append midnight so the string parses in local time).
    const parsedData = data.map((d) => ({
        ...d,
        timestamp: new Date(`${d.date}T00:00:00`).getTime(),
    }));

    // Anchor all period windows to the LAST available server date (Fix 3).
    const today = new Date(parsedData[parsedData.length - 1].timestamp);

    let cutoff = new Date(0); // MAX → everything from inception
    switch (period) {
        case '7D':
            cutoff = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
            break;
        case '1M':
            cutoff = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
            break;
        case '3M':
            cutoff = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
            break;
        case '6M':
            cutoff = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
            break;
        case '1Y':
            cutoff = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
            break;
        case 'YTD':
            // Backend anchors YTD to the value on/before Dec 31 of the prior
            // year. `new Date(year, 0, 0)` IS Dec 31 of the previous year (Fix 1).
            cutoff = new Date(today.getFullYear(), 0, 0);
            break;
    }
    const cutoffTime = cutoff.getTime();

    // Baseline = the LAST point ON/BEFORE the cutoff (Fix 2), scanning the full
    // parsed array — not the first point AFTER the cutoff. Falls back to the
    // first element when the whole series starts after the cutoff.
    let baseIdx = -1;
    for (let i = parsedData.length - 1; i >= 0; i--) {
        if (parsedData[i].timestamp <= cutoffTime) {
            baseIdx = i;
            break;
        }
    }
    if (baseIdx < 0) baseIdx = 0;

    const basePortfolio = parsedData[baseIdx].portfolio_twr || 1.0;
    const baseBenchmark = parsedData[baseIdx].benchmark_value || 1.0;

    // Period window for the chart curve.
    let subset = parsedData.filter((d) => d.timestamp >= cutoffTime);
    if (subset.length === 0) subset = parsedData; // Fallback if period > portfolio age

    const chartData: RebasedChartPoint[] = subset.map((d) => {
        const dObj = new Date(d.timestamp);
        return {
            date: d.date,
            formattedDate: dObj.toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: (['MAX', '1Y', 'YTD'] as Period[]).includes(period)
                    ? '2-digit'
                    : undefined,
            }),
            Portfolio: (d.portfolio_twr / basePortfolio - 1) * 100,
            Benchmark: (d.benchmark_value / baseBenchmark - 1) * 100,
        };
    });

    const last = parsedData[parsedData.length - 1];
    const portPeriodPct = (last.portfolio_twr / basePortfolio - 1) * 100;
    const benchPeriodPct = (last.benchmark_value / baseBenchmark - 1) * 100;

    return { chartData, portPeriodPct, benchPeriodPct };
};
